import HologardeBackground from '@assets/hologarde-background.jpg';
import { Button, Center, createListCollection, Flex, Image, Spinner, Text, VStack } from '@chakra-ui/react';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useAppDispatch, useAppSelector } from '@hooks/redux.hooks';
import { useWithDispatch } from '@hooks/useWithDispatch';
import {
  updateActiveUser,
  updateHasSelectedGroup,
  updateRenewalToken,
  updateSessionUuid,
  updateToken,
  userLogout,
} from '@redux/authent/authent.reducer';
import { selectActiveUser, selectHasSelectedGroup } from '@redux/authent/authent.selectors';
import { usePatchPersonalActiveGroupMutation } from '@services/authent/user.api';
import { isSyriusExceptionPayload } from '@utils/common.utils';
import { ReactNode, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { GroupLight } from '@/types/authent/groups.types';

import { CustomSelect } from './ui/select/Select';

type Props = {
  children: ReactNode;
};

export default function UserGroupInit({ children }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();

  const hasSelectedGroup = useAppSelector(selectHasSelectedGroup);
  const activeUser = useAppSelector(selectActiveUser);

  const [hasChosenGroup, setHasChosenGroup] = useState<boolean>(hasSelectedGroup);
  const [selectedGroup, setSelectedGroup] = useState<GroupLight | null>(activeUser.activeGroup);

  const updateHasSelectedActiveGroup = useWithDispatch(updateHasSelectedGroup);
  const updateActiveUserAction = useWithDispatch(updateActiveUser);
  const [patchPersonalActiveGroup, { isLoading }] = usePatchPersonalActiveGroupMutation();

  const applyActiveGroupChange = (data: {
    token: string | null;
    renew: string | null;
    sessionUuid: string | null;
    user: typeof activeUser;
  }) => {
    dispatch(updateToken(data.token));
    dispatch(updateRenewalToken(data.renew));
    dispatch(updateSessionUuid(data.sessionUuid));
    updateActiveUserAction(data.user);
    setHasChosenGroup(true);
    updateHasSelectedActiveGroup(true);
  };

  const handleLoginException = (error: unknown) => {
    if (isSyriusExceptionPayload(error) && error.data.code === 'common.invalid_parameter' && error.data.params[0]) {
      createToast(
        formatMessage({ id: 'authent.userGroupInit.missingParameter' }, { field: error.data.params[0].i18nKey }),
        ToastStatusEnum.ERROR,
      );
    }
  };

  if (activeUser.groups.length === 0) {
    dispatch(userLogout());
    createToast(formatMessage({ id: 'authent.userGroupInit.noGroup' }), ToastStatusEnum.ERROR);
    return null;
  }

  if (activeUser.groups.length === 1 && !hasChosenGroup && !isLoading) {
    setHasChosenGroup(true);
    patchPersonalActiveGroup({ newActiveGroupId: activeUser.groups[0].id })
      .unwrap()
      .then(applyActiveGroupChange)
      .catch((error) => {
        dispatch(userLogout());
        handleLoginException(error);
      });
    return null;
  }

  if (!isLoading && hasSelectedGroup && activeUser.activeGroup) {
    return <>{children}</>;
  }

  function handleClick() {
    if (selectedGroup) {
      patchPersonalActiveGroup({ newActiveGroupId: selectedGroup.id })
        .unwrap()
        .then(applyActiveGroupChange)
        .catch((error) => handleLoginException(error));
    }
  }
  const items = createListCollection({
    items: activeUser.groups.map((group) => ({ value: group.code, label: group.name })),
  });

  return (
    <Flex width="100%" height="100%" alignContent="center" justifyContent="center">
      <Image src={HologardeBackground} fit="cover" height="100%" width="100%" position="absolute" />
      {hasChosenGroup && !hasSelectedGroup ? (
        <Center>
          <Spinner size="xl" color="neutral.white" />
        </Center>
      ) : (
        <VStack
          position="fixed"
          right={0}
          alignItems="start"
          gap={3}
          width="424px"
          height="100%"
          padding={8}
          justifyContent="center"
          backgroundColor="neutral.900-op30"
        >
          <VStack gap={1} alignItems="start">
            <Text textStyle="md" lineHeight="24px">
              <FormattedMessage id="authent.userGroupInit.title" />
            </Text>
            <CustomSelect
              value={selectedGroup?.code}
              collection={items}
              width="296px"
              size="sm"
              onChange={(value) => setSelectedGroup(activeUser.groups.find((group) => group.code === value) ?? null)}
            />
          </VStack>
          <Button
            width="100%"
            size="md"
            loading={isLoading}
            spinner={<Spinner size="md" color="neutral.white" />}
            disabled={!selectedGroup}
            onClick={handleClick}
          >
            <FormattedMessage id="global.validate" />
          </Button>
        </VStack>
      )}
    </Flex>
  );
}
